import * as Yup from 'yup';
import {
  LATIN_LETTERS_AND_DIGITS_REG_EXP,
  START_WITH_GB_REG_EXP,
  getCustomerNameRegExp,
  validateCommentFieldByPaymentMethod
} from './utils';
import { PAYMENT_METHOD, PAYMENT_PROVIDERS, RECIPIENT_TYPES } from 'components/common/constants';
import { getMaxDescriptionSize } from 'components/common/PaymentForm/utils';
import { trimTextContent } from 'services/utils';

export default ({
  i18n,
  onChange,
  onSubmit,
  customerNumber,
  model,
  currency,
  paymentMethod,
  previousTransaction,
  providerData
}) => ({
  enableReinitialize: true,
  validateOnChange: false,
  validateOnBlur: false,
  initialValues: {
    amount: `${previousTransaction?.amount || providerData?.amount || ''}`,
    recipient_wallet: previousTransaction?.counterparty?.iban || providerData?.recipient_wallet || '',
    recipient_type:
      RECIPIENT_TYPES[previousTransaction?.recipient_type] ||
      RECIPIENT_TYPES[previousTransaction?.counterparty?.party_type] ||
      providerData?.recipient_type ||
      RECIPIENT_TYPES.COMPANY,
    company_name: previousTransaction?.counterparty?.party_name || providerData?.company_name,
    first_name:
      (previousTransaction?.counterparty?.first_name ? previousTransaction?.counterparty?.first_name : null) ||
      providerData?.first_name,
    last_name:
      (previousTransaction?.counterparty?.last_name ? previousTransaction?.counterparty?.last_name : null) ||
      providerData?.last_name,
    comment: previousTransaction?.comment || providerData?.comment,
    documents: null
  },
  initialStatus: { company_name: true, recipient_wallet: true },
  validationSchema: Yup.object().shape(
    {
      recipient_wallet: Yup.string()
        .nullable(true)
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(34, i18n.getMessage('client.validation.message.maxSize', { size: 34 }))
        .trim()
        // trick for using `currency` variable in checking value in validation
        .when('recipient_type', (recipient_type, schema) => {
          if (currency === 'GBP' && paymentMethod !== PAYMENT_METHOD.SWIFT) {
            return schema.matches(START_WITH_GB_REG_EXP, {
              message: i18n.getMessage('client.validation.message.startWithGB')
            });
          }
          return schema;
        })
        .matches(LATIN_LETTERS_AND_DIGITS_REG_EXP, {
          message: i18n.getMessage('client.validation.message.isOnlyLatinLettersAndDigits')
        }),
      amount: Yup.string().nullable(true).required(i18n.getMessage('client.validation.message.isEmpty')),
      recipient_type: Yup.string().nullable(false).required(i18n.getMessage('client.validation.message.isEmpty')),
      company_name: Yup.string()
        .nullable(true)
        .when('recipient_type', {
          is: RECIPIENT_TYPES.COMPANY,
          then: (schema) =>
            schema
              .required(i18n.getMessage('client.validation.message.isEmpty'))
              .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
              .matches(getCustomerNameRegExp(), {
                message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                  availableSymbols: "“.“, “,“, “-“, “/“, “:“, “?“, “(“, “)“, “'“, “+“, “CrLf“, “Space“"
                })
              }),
          otherwise: (schema) => schema.nullable(true)
        }),
      first_name: Yup.string()
        .nullable(true)
        .when('recipient_type', {
          is: RECIPIENT_TYPES.INDIVIDUAL,
          then: (schema) =>
            schema
              .required(i18n.getMessage('client.validation.message.isEmpty'))
              .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
              .matches(getCustomerNameRegExp(), {
                message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                  availableSymbols: "“.“, “,“, “-“, “/“, “:“, “?“, “(“, “)“, “'“, “+“, “CrLf“, “Space“"
                })
              }),
          otherwise: (schema) => schema.nullable(true)
        }),
      last_name: Yup.string()
        .nullable(true)
        .when('recipient_type', {
          is: RECIPIENT_TYPES.INDIVIDUAL,
          then: (schema) =>
            schema
              .required(i18n.getMessage('client.validation.message.isEmpty'))
              .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
              .matches(getCustomerNameRegExp(), {
                message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                  availableSymbols: "“.“, “,“, “-“, “/“, “:“, “?“, “(“, “)“, “'“, “+“, “CrLf“, “Space“"
                })
              }),
          otherwise: (schema) => schema.nullable(true)
        }),
      comment: Yup.string()
        .nullable(true)
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .test(
          'lpb-comment-validation',
          paymentMethod === PAYMENT_METHOD.SWIFT
            ? i18n.getMessage('transfer.form.error.comment.SWIFT')
            : paymentMethod === PAYMENT_METHOD.FASTER_PAYMENTS
              ? i18n.getMessage('transfer.form.error.comment.FASTER_PAYMENTS')
              : i18n.getMessage('transfer.form.error.comment.OTHER_METHODS'),
          (comment) => validateCommentFieldByPaymentMethod(paymentMethod, trimTextContent(comment))
        )
        .max(
          getMaxDescriptionSize(PAYMENT_PROVIDERS.LPB, paymentMethod, currency),
          i18n.getMessage('client.validation.message.maxSize', {
            size: getMaxDescriptionSize(PAYMENT_PROVIDERS.LPB, paymentMethod, currency)
          })
        )
    },
    [['account_number', 'recipient_wallet']]
  ),
  onSubmit: (values) => {
    onChange(values);
    onSubmit({ customerNumber, providerData: values, model });
  }
});
